import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const PmvReliabilityAttentionPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>('OPEN');
  const [severity, setSeverity] = useState<string | undefined>();

  const q = useQuery({
    queryKey: ['pmv-reliability-attention', status],
    queryFn: () =>
      pmvService.listReliabilityAttention({
        status,
        limit: 100,
      }),
  });

  const items = useMemo(() => {
    const rows = q.data?.items ?? [];
    if (!severity) return rows;
    return rows.filter((r: { severity?: string }) => r.severity === severity);
  }, [q.data, severity]);

  const ack = useMutation({
    mutationFn: (id: string) => pmvService.ackReliabilityAttention(id),
    onSuccess: () => {
      message.success('Acknowledged');
      void qc.invalidateQueries({ queryKey: ['pmv-reliability-attention'] });
    },
  });

  const dismiss = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      pmvService.dismissReliabilityAttention(id, { dismiss_reason: reason }),
    onSuccess: () => {
      message.success('Dismissed');
      void qc.invalidateQueries({ queryKey: ['pmv-reliability-attention'] });
    },
  });

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Reliability Attention"
        subtitle="Aggregated advisory attention — not one alert per telemetry pulse"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/reliability/control-tower')}>
              Control Tower
            </Button>
            <Button onClick={() => navigate('/asset/pmv/reliability')}>Health Register</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/failures')}>Failures</Button>
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder="Severity"
              style={{ width: 140 }}
              value={severity}
              onChange={setSeverity}
              options={['CRITICAL', 'WARNING', 'INFO'].map((v) => ({ value: v, label: v }))}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={status}
              onChange={setStatus}
              options={['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'DISMISSED'].map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Reliability observes and advises — Maintenance owns cases. Attention is aggregated, not one row per telemetry pulse."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No reliability attention for these filters" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1200 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Equipment',
              dataIndex: 'equipment_label',
              width: 200,
              ellipsis: true,
              render: (v, r) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}
                  >
                    {v || 'Equipment'}
                  </Button>
                </div>
              ),
            },
            { title: 'Code', dataIndex: 'attention_code', width: 160, ellipsis: true },
            {
              title: 'Severity',
              dataIndex: 'severity',
              width: 100,
              render: (v) => (
                <Tag color={v === 'CRITICAL' ? 'red' : v === 'WARNING' ? 'orange' : 'blue'}>
                  {v}
                </Tag>
              ),
            },
            { title: 'Title', dataIndex: 'title', ellipsis: true },
            {
              title: 'Confidence',
              dataIndex: 'confidence',
              width: 110,
              render: (v) => v || '—',
            },
            {
              title: 'Action',
              dataIndex: 'recommended_action',
              width: 140,
              ellipsis: true,
              render: (v) => v || '—',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (v) => <Tag>{v}</Tag>,
            },
            {
              title: 'Actions',
              width: 280,
              render: (_, r) => (
                <Space wrap size="small">
                  <Button
                    size="small"
                    onClick={() => ack.mutate(r.id)}
                    disabled={r.status !== 'OPEN'}
                  >
                    Ack
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      let reason = '';
                      Modal.confirm({
                        title: 'Dismiss attention',
                        content: (
                          <Input.TextArea
                            rows={3}
                            placeholder="Reason required"
                            onChange={(e) => {
                              reason = e.target.value;
                            }}
                          />
                        ),
                        onOk: () => {
                          if (!reason.trim()) {
                            message.error('Reason required');
                            return Promise.reject();
                          }
                          return dismiss.mutateAsync({ id: r.id, reason });
                        },
                      });
                    }}
                  >
                    Dismiss
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      void pmvService
                        .openReliabilityMaintenance(r.pmv_profile_id, {
                          attention_id: r.id,
                          title: r.title,
                        })
                        .then(() => message.success('Maintenance case opened'))
                        .catch((e) => message.error(String(e?.message || e)))
                    }
                  >
                    Open Maintenance
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvReliabilityAttentionPage;
